/**
 * A table that pages at 200 rows and virtualises above them, keeping selection and focus (master §10.4).
 *
 * Rows are reconciled by key: a row's `<tr>` survives every live update (only its cells change and it
 * moves), and while a pointer is pressed on the body no update is applied, so the row under an
 * in-progress click never resorts (UI spec §5; INC-0612 F7). The held rows apply on release.
 */
import { PAGE_ROWS_MAX } from "../contracts/constants.ts";
import { h, setChildren, type Child } from "./dom.ts";

export interface Column<R> {
  readonly key: string;
  readonly header: string;
  readonly render: (row: R) => Child;
}

export interface TableOptions<R> {
  readonly label: string;
  readonly columns: readonly Column<R>[];
  readonly rowKey: (row: R) => string;
  readonly rowHeight?: number;
  readonly viewportRows?: number;
  readonly overscan?: number;
  readonly threshold?: number;
  readonly onActivate?: (row: R) => void;
}

export interface TableHandle<R> {
  readonly element: HTMLElement;
  setRows(rows: readonly R[]): void;
  /** The key of the selected row, or `null`. */
  selected(): string | null;
  /** How many `<tr>` body rows are in the DOM right now. */
  renderedRows(): number;
}

export interface Window {
  readonly start: number;
  readonly end: number;
}

/** The rows to render: those in view plus an overscan on each side. */
export function windowRange(
  total: number,
  scrollTop: number,
  viewportHeight: number,
  rowHeight: number,
  overscan: number,
): Window {
  const first = Math.floor(scrollTop / rowHeight);
  const last = Math.ceil((scrollTop + viewportHeight) / rowHeight);
  return { start: Math.max(0, first - overscan), end: Math.min(total, last + overscan) };
}

/** Create the table; above `threshold` rows only the visible window exists in the DOM. */
export function createTable<R>(options: TableOptions<R>): TableHandle<R> {
  const rowHeight = options.rowHeight ?? 32;
  const viewportHeight = (options.viewportRows ?? 12) * rowHeight;
  const overscan = options.overscan ?? 4;
  const threshold = options.threshold ?? PAGE_ROWS_MAX;
  let rows: readonly R[] = [];
  let selectedKey: string | null = null;
  let rendered = 0;

  const head = h(
    "thead",
    {},
    h(
      "tr",
      {},
      ...options.columns.map((column) => h("th", { attrs: { scope: "col" } }, column.header)),
    ),
  );
  const body = h("tbody");
  const table = h("table", { attrs: { role: "grid", "aria-label": options.label } }, head, body);
  const wrap = h("div", { class: "table-wrap" }, table);

  const spacer = (height: number): HTMLElement =>
    h(
      "tr",
      { class: "spacer", attrs: { "aria-hidden": "true" } },
      h("td", {
        attrs: { colspan: String(options.columns.length), style: `height:${String(height)}px` },
      }),
    );

  /** The `<tr>` of each rendered key, re-used by the next render. */
  let byKey = new Map<string, HTMLElement>();
  /** Whether a pointer is pressed on the body, and whether rows arrived while it was. */
  let pressed = false;
  let held = false;

  const rowNode = (row: R, key: string, index: number, tabbable: string | null): HTMLElement => {
    const node =
      byKey.get(key) ??
      h("tr", { attrs: { "data-row-key": key } }, ...options.columns.map(() => h("td")));
    node.setAttribute("tabindex", key === tabbable ? "0" : "-1");
    node.setAttribute("aria-rowindex", String(index + 2));
    node.setAttribute("aria-selected", String(key === selectedKey));
    options.columns.forEach((column, at) => {
      setChildren(node.children[at] as HTMLElement, [column.render(row)]);
    });
    return node;
  };

  const render = (focus: boolean): void => {
    const virtual = rows.length > threshold;
    const view = virtual
      ? windowRange(rows.length, wrap.scrollTop, viewportHeight, rowHeight, overscan)
      : { start: 0, end: rows.length };
    wrap.style.maxHeight = virtual ? `${String(viewportHeight)}px` : "";
    wrap.style.overflowY = virtual ? "auto" : "";
    const active = document.activeElement;
    const focusedKey =
      active instanceof HTMLElement && body.contains(active) ? active.dataset.rowKey : undefined;
    const fallback = rows[0] === undefined ? null : options.rowKey(rows[0]);
    const tabbable = selectedKey ?? fallback;
    const nodes: HTMLElement[] = [];
    const next = new Map<string, HTMLElement>();
    if (virtual) {
      nodes.push(spacer(view.start * rowHeight));
    }
    for (let index = view.start; index < view.end; index += 1) {
      const row = rows[index] as R;
      const key = options.rowKey(row);
      const node = rowNode(row, key, index, tabbable);
      next.set(key, node);
      nodes.push(node);
    }
    if (virtual) {
      nodes.push(spacer((rows.length - view.end) * rowHeight));
    }
    byKey = next;
    rendered = view.end - view.start;
    // Move only what is out of place, then drop what is no longer shown.
    nodes.forEach((node, at) => {
      if (body.children.item(at) !== node) {
        body.insertBefore(node, body.children.item(at));
      }
    });
    for (const stale of [...body.children].slice(nodes.length)) {
      stale.remove();
    }
    table.setAttribute("aria-rowcount", String(rows.length + 1));
    const wanted = focus ? selectedKey : focusedKey;
    if (wanted !== null && wanted !== undefined) {
      byKey.get(wanted)?.focus();
    }
  };

  /** Hold updates from a press on the body until the pointer is released anywhere (UI spec §5). */
  const press = (): void => {
    if (pressed) {
      return;
    }
    pressed = true;
    const release = new AbortController();
    const done = (): void => {
      release.abort();
      pressed = false;
      if (held) {
        held = false;
        render(false);
      }
    };
    for (const type of ["pointerup", "pointercancel", "mouseup"]) {
      body.ownerDocument.addEventListener(type, done, { signal: release.signal });
    }
  };
  body.addEventListener("pointerdown", press);
  body.addEventListener("mousedown", press);

  const select = (index: number): void => {
    const bounded = Math.max(0, Math.min(rows.length - 1, index));
    const row = rows[bounded];
    if (row === undefined) {
      return;
    }
    selectedKey = options.rowKey(row);
    if (rows.length > threshold) {
      const top = bounded * rowHeight;
      if (top < wrap.scrollTop || top + rowHeight > wrap.scrollTop + viewportHeight) {
        wrap.scrollTop = top;
      }
    }
    render(true);
  };

  wrap.addEventListener("scroll", () => {
    if (rows.length > threshold) {
      render(false);
    }
  });
  body.addEventListener("keydown", (event) => {
    const current = rows.findIndex((row) => options.rowKey(row) === selectedKey);
    const moves = new Map<string, number>([
      ["ArrowDown", current + 1],
      ["ArrowUp", current - 1],
      ["Home", 0],
      ["End", rows.length - 1],
    ]);
    const target = moves.get(event.key);
    if (target !== undefined) {
      event.preventDefault();
      select(target);
    } else if (event.key === "Enter" && current >= 0) {
      options.onActivate?.(rows[current] as R);
    }
  });
  body.addEventListener("focusin", (event) => {
    const key = (event.target as HTMLElement).dataset.rowKey;
    if (key !== undefined) {
      selectedKey = key;
    }
  });

  return {
    element: wrap,
    setRows: (next) => {
      rows = next;
      if (selectedKey !== null && !rows.some((row) => options.rowKey(row) === selectedKey)) {
        selectedKey = null;
      }
      if (pressed) {
        held = true;
        return;
      }
      render(false);
    },
    selected: () => selectedKey,
    renderedRows: () => rendered,
  };
}
